<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <div class="d-flex justify-space-between align-center mb-6">
          <div>
            <h1 class="text-h4 mb-2">Questionnaires</h1>
            <p class="text-subtitle-1 text-grey">
              Complétez les questionnaires requis pour vos candidatures
            </p>
          </div>
          <v-chip :color="getStatusColor()" variant="tonal">
            {{ completedCount }}/{{ questionnaires.length }} complétés
          </v-chip>
        </div>
      </v-col>
    </v-row>

    <!-- Filtres -->
    <v-row class="mb-4">
      <v-col cols="12" md="4">
        <v-select v-model="statusFilter" :items="statusOptions" label="Filtrer par statut" variant="outlined"
          density="compact" clearable></v-select>
      </v-col>
      <v-col cols="12" md="4">
        <v-select v-model="typeFilter" :items="typeOptions" label="Type de questionnaire" variant="outlined"
          density="compact" clearable></v-select>
      </v-col>
    </v-row>

    <!-- Liste des questionnaires -->
    <v-row v-if="!loading">
      <v-col v-for="questionnaire in filteredQuestionnaires" :key="questionnaire.id" cols="12" md="6" lg="4">
        <v-card class="questionnaire-card" :class="{
          'completed': questionnaire.status === 'completed',
          'urgent': isUrgent(questionnaire.dueDate),
          'overdue': isOverdue(questionnaire.dueDate)
        }" elevation="2" @click="openQuestionnaire(questionnaire)">
          <v-card-title class="d-flex align-center">
            <v-icon :color="getQuestionnaireStatusColor(questionnaire.status)" class="me-2">
              {{ getQuestionnaireIcon(questionnaire.status) }}
            </v-icon>
            <span class="text-truncate">{{ questionnaire.title }}</span>
          </v-card-title>

          <v-card-subtitle class="d-flex justify-space-between">
            <span>{{ questionnaire.jobPosition }}</span>
            <v-chip v-if="questionnaire.company" size="x-small" variant="outlined">
              {{ questionnaire.company }}
            </v-chip>
          </v-card-subtitle>

          <v-card-text>
            <div class="mb-3 d-flex flex-wrap gap-2">
              <v-chip :color="getQuestionnaireStatusColor(questionnaire.status)" size="small" variant="tonal">
                {{ getStatusText(questionnaire.status) }}
              </v-chip>
              <v-chip color="info" size="small" variant="outlined">
                {{ questionnaire.type }}
              </v-chip>
              <v-chip v-if="questionnaire.difficulty" :color="difficultyColors[questionnaire.difficulty]" size="small"
                variant="text">
                {{ getDifficultyText(questionnaire.difficulty) }}
              </v-chip>
            </div>

            <p class="text-body-2 mb-3">{{ questionnaire.description }}</p>

            <div class="d-flex justify-space-between align-center mb-2">
              <div class="text-caption text-grey">
                <v-icon size="16" class="me-1">mdi-clock-outline</v-icon>
                {{ questionnaire.estimatedTime }}
              </div>
              <div class="text-caption text-grey">
                <v-icon size="16" class="me-1">mdi-help-circle-outline</v-icon>
                {{ questionnaire.questions }} questions
              </div>
            </div>

            <!-- Score pour les questionnaires terminés -->
            <div v-if="questionnaire.status === 'completed' && questionnaire.score" class="mb-2 d-flex align-center">
              <v-icon size="16" class="me-1 text-success">mdi-trophy</v-icon>
              <span class="text-caption font-weight-medium">
                Score: {{ questionnaire.score }}/100
              </span>
            </div>

            <!-- Barre de progression -->
            <v-progress-linear v-if="questionnaire.status === 'in_progress' || questionnaire.status === 'completed'"
              :model-value="questionnaire.progress"
              :color="questionnaire.status === 'completed' ? 'success' : 'primary'" class="mt-3" height="6"
              rounded></v-progress-linear>

            <!-- Date limite -->
            <div v-if="questionnaire.dueDate" class="mt-3 d-flex align-center" :class="{
              'text-error': isOverdue(questionnaire.dueDate),
              'text-warning': isUrgent(questionnaire.dueDate)
            }">
              <v-icon size="16" class="me-1"
                :icon="isOverdue(questionnaire.dueDate) ? 'mdi-calendar-alert' : 'mdi-calendar-clock'"></v-icon>
              <span class="text-caption">
                {{ isOverdue(questionnaire.dueDate) ? 'Échéance dépassée:' : 'Échéance:' }}
                {{ formatDate(questionnaire.dueDate) }}
              </span>
            </div>
          </v-card-text>

          <v-card-actions>
            <v-btn v-if="questionnaire.status === 'pending'" color="primary" variant="tonal"
              @click.stop="startQuestionnaire(questionnaire)">
              Commencer
            </v-btn>
            <v-btn v-else-if="questionnaire.status === 'in_progress'" color="warning" variant="tonal"
              @click.stop="continueQuestionnaire(questionnaire)">
              Continuer ({{ questionnaire.progress }}%)
            </v-btn>
            <v-btn v-else-if="questionnaire.status === 'completed'" color="success" variant="outlined"
              @click.stop="viewResults(questionnaire)">
              Voir résultats
            </v-btn>

            <v-spacer></v-spacer>

            <v-btn icon="mdi-information-outline" variant="text" size="small"
              @click.stop="showInfo(questionnaire)"></v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>

    <!-- État de chargement -->
    <v-row v-else>
      <v-col cols="12" class="text-center py-8">
        <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
        <p class="mt-4 text-h6">Chargement des questionnaires...</p>
      </v-col>
    </v-row>

    <!-- Message si aucun questionnaire -->
    <v-row v-if="!loading && filteredQuestionnaires.length === 0">
      <v-col cols="12" class="text-center py-8">
        <v-icon size="64" color="grey-lighten-1">mdi-clipboard-text-outline</v-icon>
        <p class="mt-4 text-h6 text-grey">Aucun questionnaire trouvé</p>
        <p class="text-body-2 text-grey">
          {{ statusFilter || typeFilter ? 'Essayez de modifier vos filtres' : 'Vous n\'avez pas encore de questionnaires assignés' }}
        </p>
      </v-col>
    </v-row>

    <!-- Dialog questionnaire -->
    <v-dialog v-model="questionnaireDialog" max-width="800px" persistent>
      <v-card v-if="currentQuestionnaire">
        <v-card-title class="d-flex justify-space-between align-center">
          <span>{{ currentQuestionnaire.title }}</span>
          <v-chip color="primary" size="small">
            Question {{ currentQuestionIndex + 1 }}/{{ currentQuestionnaire.questionsData.length }}
          </v-chip>
        </v-card-title>

        <v-progress-linear :model-value="(currentQuestionIndex + 1) / currentQuestionnaire.questionsData.length * 100"
          color="primary" height="4"></v-progress-linear>

        <v-card-text class="pt-6">
          <div v-if="currentQuestion" class="question-container">
            <div class="d-flex align-center mb-4">
              <v-chip :color="currentQuestion.required ? 'error' : 'info'" size="small" variant="outlined" class="me-2">
                {{ currentQuestion.required ? 'Obligatoire' : 'Optionnelle' }}
              </v-chip>
              <v-chip color="primary" size="small" variant="text">
                {{ currentQuestion.type === 'multiple_choice' ? 'Choix unique' :
                  currentQuestion.type === 'checkbox' ? 'Choix multiples' :
                    currentQuestion.type === 'text' ? 'Réponse libre' :
                      currentQuestion.type === 'rating' ? 'Évaluation' : currentQuestion.type }}
              </v-chip>
            </div>

            <h3 class="text-h6 mb-4 question-text">{{ currentQuestion.question }}</h3>

            <!-- Question à choix multiple -->
            <v-radio-group v-if="currentQuestion.type === 'multiple_choice'" v-model="currentAnswer" class="mt-4">
              <v-radio v-for="option in currentQuestion.options" :key="option.id" :label="option.text"
                :value="option.id" class="mb-2"></v-radio>
            </v-radio-group>

            <!-- Question à choix multiples -->
            <div v-else-if="currentQuestion.type === 'checkbox'" class="mt-4">
              <p class="text-body-2 text-grey mb-3">Vous pouvez sélectionner plusieurs réponses:</p>
              <v-checkbox v-for="option in currentQuestion.options" :key="option.id" v-model="currentAnswer"
                :label="option.text" :value="option.id" multiple class="mb-1"></v-checkbox>
            </div>

            <!-- Question ouverte -->
            <v-textarea v-else-if="currentQuestion.type === 'text'" v-model="currentAnswer"
              :label="currentQuestion.placeholder || 'Votre réponse'" variant="outlined" rows="4" class="mt-4"
              counter="500" persistent-counter></v-textarea>

            <!-- Question d'évaluation -->
            <div v-else-if="currentQuestion.type === 'rating'" class="mt-4">
              <div class="text-center mb-4">
                <v-rating v-model="currentAnswer" :length="currentQuestion.maxRating || 5" color="primary" size="large"
                  hover></v-rating>
              </div>
              <div class="d-flex justify-space-between">
                <span class="text-caption text-grey">{{ currentQuestion.minLabel }}</span>
                <span class="text-caption text-grey">{{ currentQuestion.maxLabel }}</span>
              </div>
              <div v-if="currentAnswer" class="text-center mt-2">
                <v-chip color="primary" size="small">
                  {{ currentAnswer }}/{{ currentQuestion.maxRating || 5 }}
                </v-chip>
              </div>
            </div>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-btn variant="outlined" @click="closeQuestionnaire">
            Fermer
          </v-btn>

          <v-spacer></v-spacer>

          <v-btn v-if="currentQuestionIndex > 0" variant="outlined" @click="previousQuestion">
            Précédent
          </v-btn>

          <v-btn v-if="currentQuestionIndex < currentQuestionnaire.questionsData.length - 1" color="primary"
            @click="nextQuestion" :disabled="!hasValidAnswer">
            Suivant
          </v-btn>

          <v-btn v-else color="success" @click="finishQuestionnaire" :disabled="!hasValidAnswer">
            Terminer
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog informations -->
    <v-dialog v-model="infoDialog" max-width="600px">
      <v-card v-if="selectedQuestionnaire">
        <v-card-title>{{ selectedQuestionnaire.title }}</v-card-title>
        <v-card-text>
          <div class="mb-4">
            <h4 class="text-subtitle-1 mb-2">Description:</h4>
            <p>{{ selectedQuestionnaire.description }}</p>
          </div>

          <div class="mb-4">
            <h4 class="text-subtitle-1 mb-2">Détails:</h4>
            <v-list density="compact">
              <v-list-item>
                <v-list-item-title>Poste concerné</v-list-item-title>
                <v-list-item-subtitle>{{ selectedQuestionnaire.jobPosition }}</v-list-item-subtitle>
              </v-list-item>
              <v-list-item v-if="selectedQuestionnaire.company">
                <v-list-item-title>Entreprise</v-list-item-title>
                <v-list-item-subtitle>{{ selectedQuestionnaire.company }}</v-list-item-subtitle>
              </v-list-item>
              <v-list-item>
                <v-list-item-title>Nombre de questions</v-list-item-title>
                <v-list-item-subtitle>{{ selectedQuestionnaire.questions }}</v-list-item-subtitle>
              </v-list-item>
              <v-list-item>
                <v-list-item-title>Temps estimé</v-list-item-title>
                <v-list-item-subtitle>{{ selectedQuestionnaire.estimatedTime }}</v-list-item-subtitle>
              </v-list-item>
              <v-list-item v-if="selectedQuestionnaire.difficulty">
                <v-list-item-title>Difficulté</v-list-item-title>
                <v-list-item-subtitle>{{ getDifficultyText(selectedQuestionnaire.difficulty) }}</v-list-item-subtitle>
              </v-list-item>
              <v-list-item v-if="selectedQuestionnaire.dueDate">
                <v-list-item-title>Date limite</v-list-item-title>
                <v-list-item-subtitle>{{ formatDate(selectedQuestionnaire.dueDate) }}</v-list-item-subtitle>
              </v-list-item>
            </v-list>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="infoDialog = false">
            Fermer
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog résultats -->
    <v-dialog v-model="resultsDialog" max-width="700px">
      <v-card v-if="selectedQuestionnaire">
        <v-card-title class="d-flex align-center">
          <v-icon color="success" class="me-2">mdi-trophy</v-icon>
          <span>Résultats: {{ selectedQuestionnaire.title }}</span>
        </v-card-title>

        <v-card-text>
          <div class="text-center mb-6">
            <v-progress-circular :model-value="selectedQuestionnaire.score || 0" :size="120" :width="8"
              :color="selectedQuestionnaire.score >= 80 ? 'success' : selectedQuestionnaire.score >= 60 ? 'warning' : 'error'"
              class="mb-4">
              <span class="text-h4 font-weight-bold">
                {{ selectedQuestionnaire.score || 0 }}
              </span>
            </v-progress-circular>
            <div class="text-h6 mb-2">Score final</div>
            <v-chip
              :color="selectedQuestionnaire.score >= 80 ? 'success' : selectedQuestionnaire.score >= 60 ? 'warning' : 'error'"
              variant="tonal" size="large">
              {{ selectedQuestionnaire.score >= 80 ? 'Excellent' : selectedQuestionnaire.score >= 60 ? 'Bien' : 'À améliorer' }}
            </v-chip>
          </div>

          <v-divider class="mb-4"></v-divider>

          <div class="mb-4">
            <h4 class="text-subtitle-1 mb-3">
              <v-icon class="me-2">mdi-comment-text</v-icon>
              Feedback
            </h4>
            <v-alert
              :color="selectedQuestionnaire.score >= 80 ? 'success' : selectedQuestionnaire.score >= 60 ? 'info' : 'warning'"
              variant="tonal"
              :icon="selectedQuestionnaire.score >= 80 ? 'mdi-check-circle' : selectedQuestionnaire.score >= 60 ? 'mdi-information' : 'mdi-alert-circle'">
              {{ selectedQuestionnaire.feedback || 'Aucun feedback disponible pour le moment.' }}
            </v-alert>
          </div>

          <div class="mb-4">
            <h4 class="text-subtitle-1 mb-3">
              <v-icon class="me-2">mdi-chart-timeline</v-icon>
              Détails de la soumission
            </h4>
            <v-list density="compact">
              <v-list-item>
                <v-list-item-title>Date de soumission</v-list-item-title>
                <v-list-item-subtitle>
                  {{ selectedQuestionnaire.completedAt ? formatDate(selectedQuestionnaire.completedAt) : 'N/A' }}
                </v-list-item-subtitle>
              </v-list-item>
              <v-list-item>
                <v-list-item-title>Temps de completion</v-list-item-title>
                <v-list-item-subtitle>{{ selectedQuestionnaire.estimatedTime }}</v-list-item-subtitle>
              </v-list-item>
              <v-list-item>
                <v-list-item-title>Questions répondues</v-list-item-title>
                <v-list-item-subtitle>{{ selectedQuestionnaire.questions }}/{{ selectedQuestionnaire.questions
                }}</v-list-item-subtitle>
              </v-list-item>
            </v-list>
          </div>

          <v-divider class="mb-4"></v-divider>

          <div>
            <h4 class="text-subtitle-1 mb-3">
              <v-icon class="me-2">mdi-directions-fork</v-icon>
              Prochaines étapes
            </h4>
            <v-alert color="info" variant="outlined" icon="mdi-information">
              {{ selectedQuestionnaire.nextSteps || 'Nous reviendrons vers vous prochainement concernant la suite du processus de recrutement.' }}
            </v-alert>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-btn variant="outlined" @click="resultsDialog = false">
            Fermer
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn color="primary" variant="tonal" prepend-icon="mdi-download" @click="downloadResults">
            Télécharger
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import { useSnackbarStore } from '@/stores/snackbar'
import { format } from 'date-fns'
import { fr } from 'date-fns/locale'
import { computed, onMounted, ref } from 'vue'
// Import de l'API service depuis le fichier TypeScript
import { apiService } from '@/services/api'

const snackbarStore = useSnackbarStore()

const loading = ref(false)
const statusFilter = ref('')
const typeFilter = ref('')
const questionnaireDialog = ref(false)
const infoDialog = ref(false)
const resultsDialog = ref(false)
const currentQuestionnaire = ref(null)
const selectedQuestionnaire = ref(null)
const currentQuestionIndex = ref(0)
const currentAnswer = ref(null)
const answers = ref({})
const questionnaires = ref([])
const autoSaveEnabled = ref(true)

const statusOptions = [
  { title: 'En attente', value: 'pending' },
  { title: 'En cours', value: 'in_progress' },
  { title: 'Terminé', value: 'completed' }
]

const typeOptions = [
  { title: 'Personnalité', value: 'Personnalité' },
  { title: 'Technique', value: 'Technique' },
  { title: 'Motivation', value: 'Motivation' },
  { title: 'Compétences', value: 'Compétences' },
  { title: 'Culture', value: 'Culture' }
]

const difficultyColors = {
  easy: 'success',
  medium: 'warning',
  hard: 'error'
}

const filteredQuestionnaires = computed(() => {
  let filtered = questionnaires.value

  if (statusFilter.value) {
    filtered = filtered.filter(q => q.status === statusFilter.value)
  }

  if (typeFilter.value) {
    filtered = filtered.filter(q => q.type === typeFilter.value)
  }

  return filtered.sort((a, b) => {
    // Prioriser par urgence et statut
    const priorityOrder = { 'pending': 1, 'in_progress': 2, 'completed': 3 }
    if (a.status !== b.status) {
      return priorityOrder[a.status] - priorityOrder[b.status]
    }

    // Puis par date d'échéance
    if (a.dueDate && b.dueDate) {
      return new Date(a.dueDate) - new Date(b.dueDate)
    }

    return a.id - b.id
  })
})

const completedCount = computed(() => {
  return questionnaires.value.filter(q => q.status === 'completed').length
})

const currentQuestion = computed(() => {
  if (!currentQuestionnaire.value || !currentQuestionnaire.value.questionsData) return null
  return currentQuestionnaire.value.questionsData[currentQuestionIndex.value]
})

const hasValidAnswer = computed(() => {
  if (!currentAnswer.value) return false
  if (Array.isArray(currentAnswer.value) && currentAnswer.value.length === 0) return false
  return !(typeof currentAnswer.value === 'string' && currentAnswer.value.trim() === '')
})

const getStatusColor = () => {
  const total = questionnaires.value.length
  const completed = completedCount.value
  const percentage = (completed / total) * 100

  if (percentage === 100) return 'success'
  if (percentage >= 50) return 'warning'
  return 'error'
}

const getQuestionnaireStatusColor = (status) => {
  switch (status) {
    case 'completed': return 'success'
    case 'in_progress': return 'warning'
    case 'pending': return 'info'
    default: return 'grey'
  }
}

const getQuestionnaireIcon = (status) => {
  switch (status) {
    case 'completed': return 'mdi-check-circle'
    case 'in_progress': return 'mdi-clock'
    case 'pending': return 'mdi-help-circle'
    default: return 'mdi-help-circle'
  }
}

const getStatusText = (status) => {
  switch (status) {
    case 'completed': return 'Terminé'
    case 'in_progress': return 'En cours'
    case 'pending': return 'En attente'
    default: return status
  }
}

const getDifficultyText = (difficulty) => {
  switch (difficulty) {
    case 'easy': return 'Facile'
    case 'medium': return 'Moyen'
    case 'hard': return 'Difficile'
    default: return difficulty
  }
}

const formatDate = (date) => {
  return format(new Date(date), 'dd MMMM yyyy', { locale: fr })
}

const isOverdue = (dueDate) => {
  return new Date(dueDate) < new Date()
}

const isUrgent = (dueDate) => {
  const daysDiff = (new Date(dueDate) - new Date()) / (1000 * 60 * 60 * 24)
  return daysDiff <= 2 && daysDiff > 0
}

const loadQuestionnaires = async () => {
  try {
    loading.value = true
    const response = await apiService.candidateSpace.getQuestionnaires()
    questionnaires.value = response.data.questionnaires
  } catch (error) {
    snackbarStore.error('Erreur lors du chargement des questionnaires')
    console.error('Erreur questionnaires:', error)
  } finally {
    loading.value = false
  }
}

const openQuestionnaire = (questionnaire) => {
  if (questionnaire.status === 'completed') {
    viewResults(questionnaire)
    return
  }

  currentQuestionnaire.value = questionnaire
  currentQuestionIndex.value = 0
  currentAnswer.value = null
  answers.value = {}
  questionnaireDialog.value = true
}

const startQuestionnaire = async (questionnaire) => {
  try {
    questionnaire.status = 'in_progress'
    questionnaire.progress = 0
    openQuestionnaire(questionnaire)
    snackbarStore.success('Questionnaire démarré')

    // Sauvegarder l'état
    if (autoSaveEnabled.value) {
      await saveProgress()
    }
  } catch (error) {
    console.error('Erreur lors du démarrage:', error)
    snackbarStore.error('Erreur lors du démarrage du questionnaire')
  }
}

const continueQuestionnaire = (questionnaire) => {
  openQuestionnaire(questionnaire)
  snackbarStore.info('Reprise du questionnaire')
}

const viewResults = (questionnaire) => {
  selectedQuestionnaire.value = questionnaire
  resultsDialog.value = true
}

const showInfo = (questionnaire) => {
  selectedQuestionnaire.value = questionnaire
  infoDialog.value = true
}

const closeQuestionnaire = () => {
  if (currentQuestionnaire.value && currentQuestionnaire.value.status === 'in_progress') {
    saveProgress()
  }
  questionnaireDialog.value = false
  currentQuestionnaire.value = null
}

const nextQuestion = () => {
  saveCurrentAnswer()

  if (currentQuestionIndex.value < currentQuestionnaire.value.questionsData.length - 1) {
    currentQuestionIndex.value++
    loadAnswer()
    updateProgress()
  }
}

const previousQuestion = () => {
  saveCurrentAnswer()

  if (currentQuestionIndex.value > 0) {
    currentQuestionIndex.value--
    loadAnswer()
  }
}

const saveCurrentAnswer = () => {
  if (currentQuestion.value && currentAnswer.value !== null) {
    answers.value[currentQuestion.value.id] = currentAnswer.value
  }
}

const loadAnswer = () => {
  if (currentQuestion.value && answers.value[currentQuestion.value.id]) {
    currentAnswer.value = answers.value[currentQuestion.value.id]
  } else {
    currentAnswer.value = currentQuestion.value?.type === 'checkbox' ? [] : null
  }
}

const updateProgress = () => {
  if (currentQuestionnaire.value) {
    const totalQuestions = currentQuestionnaire.value.questionsData.length
    const answeredQuestions = Object.keys(answers.value).length
    currentQuestionnaire.value.progress = Math.round((answeredQuestions / totalQuestions) * 100)
  }
}

const saveProgress = async () => {
  try {
    await apiService.candidateSpace.saveQuestionnaireProgress(
      currentQuestionnaire.value.id,
      {
        progress: currentQuestionnaire.value.progress,
        currentQuestionIndex: currentQuestionIndex.value,
        answers: answers.value
      }
    )
  } catch (error) {
    console.error('Erreur sauvegarde:', error)
  }
}

const finishQuestionnaire = async () => {
  try {
    saveCurrentAnswer()
    updateProgress()

    loading.value = true
    const response = await apiService.candidateSpace.submitQuestionnaire(
      currentQuestionnaire.value.id,
      answers.value
    )

    currentQuestionnaire.value.status = 'completed'
    currentQuestionnaire.value.progress = 100
    currentQuestionnaire.value.completedAt = new Date()
    currentQuestionnaire.value.score = response.data.score
    currentQuestionnaire.value.feedback = response.data.feedback
    currentQuestionnaire.value.nextSteps = response.data.nextSteps

    questionnaireDialog.value = false
    snackbarStore.success(`Questionnaire terminé ! Score: ${response.data.score}/100`)

    // Afficher les résultats
    setTimeout(() => {
      viewResults(currentQuestionnaire.value)
    }, 1000)

  } catch (error) {
    snackbarStore.error('Erreur lors de la soumission du questionnaire')
    console.error('Erreur soumission:', error)
  } finally {
    loading.value = false
  }
}

const downloadResults = () => {
  snackbarStore.info('Fonctionnalité de téléchargement en cours de développement')
}

onMounted(() => {
  loadQuestionnaires()
})
</script>

<style scoped>
.questionnaire-card {
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  animation: slideInUp 0.3s ease-out;
}

.questionnaire-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

.questionnaire-card.completed {
  border-left: 4px solid #4caf50;
}

.questionnaire-card.urgent {
  border-left: 4px solid #ff9800;
}

.questionnaire-card.overdue {
  border-left: 4px solid #f44336;
}

.questionnaire-card.overdue::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(45deg, transparent 48%, rgba(244, 67, 54, 0.1) 48%, rgba(244, 67, 54, 0.1) 52%, transparent 52%);
  pointer-events: none;
}

.gap-2 {
  gap: 8px;
}

.v-progress-linear {
  border-radius: 4px;
}

/* Animation pour les cartes nouvellement ajoutées */
@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Styles pour les dialogues */
.v-dialog .v-card {
  overflow: visible;
}

.v-alert {
  border-radius: 8px;
}

/* Amélioration des puces */
.v-chip {
  font-weight: 500;
}

/* Style pour les questions */
.question-container {
  min-height: 200px;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .questionnaire-card {
    margin-bottom: 16px;
  }
}
</style>
